import _ from "lodash";
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import classes from "./ResponsesEdit.scss";
import {
  editResponse,
  updateLocalResponses,
} from "IBPlanner/modules/IBPlannerModule";
import update from "immutability-helper";
import { graphql, compose } from "lib/apolloClient";
import ResponseItem from "../ResponseItem";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import LockOverlay from "UIComponents/LockOverlay";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import ProfileCascade from "UIComponents/ProfileCascade";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { getPlannerFieldResponsesQuery } from "UnitPlans/modules/UnitPlanQuery";
import { getPlannerFieldResponsesFromCache } from "UnitPlans/modules/UnitPlanGraphqlHelpers";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import AddEvidenceModalWrapper from "./AddEvidenceModalWrapper";
import {
  Button,
  DatePicker,
  IconButton,
  Dialog,
  DSProvider,
} from "@toddle-edu/ds-web";
import {
  AddCircleOutlined,
  DeviceDesktopOutlined,
  GoogleDriveColored,
  LinkOutlined,
  NoteOutlined,
  OneDriveColored,
  PostTwoOutlined,
  CaretDownFilled,
} from "@toddle-edu/ds-icons";
import { hasModuleAdminAccess, MODULES } from "lib/staffPermissionUtils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { isReflectionCreatedAtEditEnabled } from "UnitPlans/modules/Utils";
import ChatbarWrapper from "UIComponents/ChatbarWrapper/ChatbarWrapper";
import { getDateAndTimeSince, generateRandomId } from "Utils";
import { getFilteredAttachmentsFromEvidencesMemoized } from "Utils/DataRestructure";
import classNames from "classnames";
import moment from "moment";
import { getFormattedStudentName } from "Utils/GetFormattedStudentName";
import { getDefaultStudentNameOrderPreference } from "modules/CommonSelectors";
import ACLStore from "lib/aclStore";

const DEFAULT_MESSAGE_DATA = {
  label: "",
  attachments: [],
  id: generateRandomId(),
};

const modalDimensions = {
  height: "fit-content",
  width: "598px",
};

const getAvatarStyles = () => ({
  shape: "square",
  size: "medium",
  color: "neutral",
  iconType: "none",
  shade: "light",
  randomizeColor: true,
});

const getResponseInputMemoized = _.memoize(
  params => ({
    id: params.id,
    type: params.type,
    uids: [params.fieldKey],
    responseFilter: {
      courseIds: params.selectedCourseIds,
      studentIds: params.studentIds,
    },
  }),
  params => JSON.stringify(params)
);

const getFilteredDropDownOptionsMemoized = _.memoize(
  params =>
    _.filter(DROP_DOWN_OPTIONS, dropdownGroup => {
      const options = _.filter(dropdownGroup.options, item => {
        if (params.userType != "staff" && item.key == "add_post") {
          return false;
        }
        return (
          !_.isEmpty(params.curriculumType) &&
          !_.includes(item.hideCurriculumTypes, params.curriculumType)
        );
      });
      return !_.isEmpty(options);
    }),
  params => JSON.stringify(params)
);

const getResponseCreationData = localResponseObj => ({
  ...localResponseObj,
  label: localResponseObj.message,
  attachments: getFilteredAttachmentsFromEvidencesMemoized({
    evidence: localResponseObj.evidence,
  }),
});

const getResponseCreationDataMemoized = _.memoize(
  getResponseCreationData,
  JSON.stringify
);

const styles = {
  attachmentListContainer: {
    gridTemplateColumns: "repeat(auto-fill, 1fr)",
  },
  chatbarContainerStyle: {
    borderRadius: "0px 0px 8px 8px",
  },
};

const DROP_DOWN_OPTIONS = [
  {
    label: "common:upload_from",
    key: "UPLOAD",
    isItemGroup: true,
    options: [
      {
        key: "upload_device",
        label: "common:device",
        icon: <DeviceDesktopOutlined variant={"subtle"} />,
        hideCurriculumTypes: [],
      },
      {
        key: "add_google_drive",
        label: "common:google_drive",
        icon: <GoogleDriveColored variant="subtle" />,
        hideCurriculumTypes: [],
      },
      {
        key: "add_one_drive",
        label: "common:one_drive",
        icon: <OneDriveColored variant="subtle" />,
        hideCurriculumTypes: [],
      },
    ],
  },
  {
    label: "common:add",
    key: "ADD",
    isItemGroup: true,
    options: [
      {
        key: "add_link",
        label: "common:link",
        icon: <LinkOutlined variant={"subtle"} />,
        hideCurriculumTypes: [],
      },
      {
        key: "add_note",
        label: "common:note",
        icon: <NoteOutlined variant="subtle" />,
        hideCurriculumTypes: [],
      },
      {
        key: "add_post",
        label: "common:post",
        icon: <PostTwoOutlined variant="subtle" />,
        hideCurriculumTypes: [CURRICULUM_TYPE_DP],
      },
    ],
  },
];

class ResponsesEdit extends PureComponent {
  constructor(props) {
    super(props);
    if (props.customRef) {
      props.customRef(this);
    }

    this.state = {
      isFocused: false,
      isEditingResponse: false,
      isFullScreenLoading: false,
      showEvidenceSelectionModal: false,
      responseFieldActive: false,
      localResponsesObjectState: this.props.localResponseObj,
      isSubmittingResponse: false,
      loadingResponseIds: [],
      showDeleteConfirmDialog: false,
      responseToDelete: null,
    };

    this.createDropDownRef = null;
    this.createResponseRef = null;
    this.createContainerRef = null;
    this.responseContainerRef = null;
  }

  componentDidMount() {
    const {
      fieldId,
      fieldLockedObject,
      onFocusInputField,
      userInfo,
    } = this.props;

    const { localResponsesObjectState } = this.state;

    if (!_.isEmpty(localResponsesObjectState[fieldId])) {
      const { id: userId } = userInfo;
      const response = localResponsesObjectState[fieldId];
      const { createdBy: { id: creatorId } = {} } = response;

      const { lockedDynamicFields } = fieldLockedObject;
      const lockedField = _.find(lockedDynamicFields, {
        id: `Reflection:${response.id}`,
      });

      if (lockedField || userId != creatorId) {
        this.emptyLocalObjectInRedux();
        return;
      }

      this.setState({ isEditingResponse: true });
      onFocusInputField({ others: { id: `Reflection:${response.id}` } });
    }
    document.addEventListener("click", this.handleBodyClick, false);
  }

  componentWillUnmount() {
    const { fieldId } = this.props;

    const { isEditingResponse, localResponsesObjectState } = this.state;
    const responseValue = _.trim(
      _.get(localResponsesObjectState, `${fieldId}.message`, "")
    );
    const selectedEvidence = _.get(
      localResponsesObjectState,
      `${fieldId}.evidence`,
      []
    );

    if (responseValue || selectedEvidence.length > 0) {
      if (isEditingResponse) {
        this.onSaveEditClicked();
      } else {
        this.onSubmitClick();
      }
    }
    document.removeEventListener("click", this.handleBodyClick);
    this.emptyLocalObjectInRedux();
  }

  // TODO Need to comment what is handled in this function
  componentDidUpdate = (prevProps, prevState) => {
    const {
      isEditingResponse,
      showEvidenceSelectionModal,
      localResponsesObjectState,
    } = this.state;

    if (prevState.showEvidenceSelectionModal != showEvidenceSelectionModal) {
      if (showEvidenceSelectionModal) {
        document.removeEventListener("click", this.handleBodyClick, false);
      } else {
        document.addEventListener("click", this.handleBodyClick);
      }
    }

    if (isEditingResponse) {
      const { fieldId: prevFieldId } = prevProps;

      const { fieldId, updateInputFieldLocally } = this.props;
      const responseObj = localResponsesObjectState[fieldId] || {};
      const prevResponseObj =
        prevState?.localResponsesObjectState[fieldId] || {};

      if (
        isEditingResponse &&
        prevFieldId == fieldId &&
        prevResponseObj != responseObj
      ) {
        if (updateInputFieldLocally) {
          updateInputFieldLocally(responseObj);
        }
      }
    }
  };

  /**
   * This function handles the response box activity when user clicks anywhere.
   * It keeps the box in focus and open when there is present some content.
   * Also it handles the cases when the create dropdown is being used actively
   */
  handleBodyClick = event => {
    const { fieldId } = this.props;
    const {
      isFocused,
      isEditingResponse,
      responseFieldActive,
      localResponsesObjectState,
    } = this.state;
    if (
      !_.isNull(this.createDropDownRef) &&
      this.createDropDownRef.isActive()
    ) {
      return false;
    }
    if (
      !isEditingResponse &&
      this.createContainerRef &&
      !this.createContainerRef.contains(event.target)
    ) {
      const responseValue = _.trim(
        _.get(localResponsesObjectState, `${fieldId}.message`, "")
      );

      const selectedEvidence = _.get(
        localResponsesObjectState,
        `${fieldId}.evidence`,
        []
      );

      /*  to prevent updating isFocused to false when DriveShare modal is open:
      get the classnames of elements from where the event has passed and 
      check if any of that element is DriveShareModal*/

      const targetClassnames = _.map(event.path, path => path.className);
      const isModalOpen = _.some(
        targetClassnames,
        item =>
          _.includes(item, "OneDrivePicker") ||
          _.includes(item, "DriveShareModal")
      );

      if (isFocused && !responseFieldActive) {
        this.setState({
          isFocused:
            !!responseValue || selectedEvidence.length > 0 || isModalOpen,
        });
      }
    }
  };

  blur = ({ field }) => {
    const { fieldId } = this.props;
    const { localResponsesObjectState } = this.state;
    const responseId = localResponsesObjectState[fieldId]?.id;

    if (responseId == _.trim(field?.id, "Reflection:")) {
      this.onCancelEdit();
    }
  };

  updateCreateDropDownRef = ref => {
    this.createDropDownRef = ref;
  };

  getResponses = () => {
    const {
      mode,
      fieldId,
      userInfo,
      responses,
      showEvidence,
      onMediaClick,
      fieldLockedObject,
      filteredDropDownOptions,
      isSinglePageAndNewUnitView,
      isReflectionField,
    } = this.props;

    const { localResponsesObjectState, loadingResponseIds } = this.state;

    const { lockedDynamicFields } = fieldLockedObject;
    const localResponseObj = localResponsesObjectState[fieldId];

    const responseFeed = _.map(responses, response => {
      const isEditingResponse =
        localResponseObj && _.isEqual(localResponseObj.id, response.id);
      const lockedField = _.find(lockedDynamicFields, {
        id: `Reflection:${response.id}`,
      });
      const isAdmin = hasModuleAdminAccess({
        moduleName: MODULES.UNIT_PLANNING.id,
      });

      const isLoading =
        isSinglePageAndNewUnitView &&
        _.includes(loadingResponseIds, response.id);

      return (
        <div
          key={response.id}
          className={classNames({
            [classes.responseItemContainer]: !isSinglePageAndNewUnitView,
            [classes.responseItemContainerV2]: isSinglePageAndNewUnitView,
          })}
        >
          {isLoading ? (
            this.getSkeletonLoader()
          ) : (
            <>
              <ResponseItem
                response={response}
                onMediaClick={onMediaClick}
                onClickEdit={this.onClickEdit}
                onSaveEditClicked={this.onSaveEditClicked}
                onCancelEdit={this.onCancelEdit}
                isActive={isEditingResponse}
                localResponseObj={isEditingResponse ? localResponseObj : {}}
                onResponseChange={this.onResponseChange}
                onDropDownOptionClick={this.onDropDownOptionClick}
                dropDownOptions={filteredDropDownOptions}
                mode={"edit"}
                onAddAttachment={this.onContentUploaded}
                onClickDelete={this.onClickDelete}
                onDeleteMediaClick={this.onDeleteEvidenceClick}
                isAdmin={isAdmin}
                userInfo={userInfo}
                createDropDownRef={this.updateCreateDropDownRef}
                onBlurCreate={this.onBlurCreate}
                onResponseUpdatedAtChange={this.onResponseUpdatedAtChange}
                testId="unitPlans-responseEdit"
                isSinglePageAndNewUnitView={isSinglePageAndNewUnitView}
                fieldId={fieldId}
                getFilteredIdsFromEvidence={this.getFilteredIdsFromEvidence}
                isReflectionField={isReflectionField}
              />
              {lockedField && (
                <LockOverlay
                  isReflectionField={isReflectionField}
                  userInfo={_.get(lockedField, "userInfo", {})}
                />
              )}
            </>
          )}
        </div>
      );
    });

    return responseFeed;
  };

  /**
   * This function extracts ids from the evidence. It provides the values for
   * both initial and passed uploaded attachments or post. Uploaded
   * attachments don't have attachment property whereas post has.
   */
  getFilteredIdsFromEvidence = ({ evidence, responseId, isPost }) => {
    const { responses } = this.props;
    const response = _.find(responses, { id: responseId });
    const currentEvidence = evidence || response.evidence;

    let filteredEvidence = [];

    if (isPost) {
      filteredEvidence = _.filter(currentEvidence, item =>
        _.has(item, "attachments")
      );
    } else {
      filteredEvidence = _.filter(
        currentEvidence,
        item => !_.has(item, "attachments")
      );
    }

    return _.map(filteredEvidence, item => item.id);
  };

  onSaveEditClicked = async () => {
    const { fieldId, editResponse, isSinglePageAndNewUnitView } = this.props;

    const { localResponsesObjectState } = this.state;

    const responseObject = localResponsesObjectState[fieldId];
    const responseId = responseObject.id;

    try {
      if (!isSinglePageAndNewUnitView) {
        this.setState({ isFullScreenLoading: true });
      } else {
        this.setState(prevState => ({
          loadingResponseIds: [...prevState.loadingResponseIds, responseId],
        }));
      }
      this.onCancelEdit();
      if (
        _.trim(responseObject.message) ||
        !_.isEmpty(responseObject.evidence)
      ) {
        const initialPostIds = this.getFilteredIdsFromEvidence({
          responseId,
          isPost: true,
        });
        const initialAttachmentIds = this.getFilteredIdsFromEvidence({
          responseId,
          isPost: false,
        });

        //if there is content we update
        await editResponse({
          fieldId,
          id: responseId,
          initialPostIds,
          initialAttachmentIds,
          responseFieldData: responseObject,
        });
      } else {
        //no content we delete the response
        this.onClickDelete({ id: responseId });
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (!isSinglePageAndNewUnitView) {
        this.setState({ isFullScreenLoading: false });
      } else {
        this.setState(prevState => ({
          loadingResponseIds: _.filter(
            prevState.loadingResponseIds,
            id => id !== responseId
          ),
        }));
      }
    }
  };

  onUpdateLocalResponseState = ({ fieldId, updatedResponseObj }) => {
    const { localResponsesObjectState } = this.state;

    this.setState({
      localResponsesObjectState: {
        ...localResponsesObjectState,
        [fieldId]: updatedResponseObj,
      },
    });
  };

  onResponseChange = params => {
    const key = _.keys(params)[0];
    //return in case of attachments as it is handled by onContentUploaded & onDeleteEvidenceClick
    if (key == "attachments") return;
    const value = params[key];

    const { fieldId } = this.props;
    const { localResponsesObjectState } = this.state;
    const responseObject = localResponsesObjectState[fieldId] || {};

    this.setState({
      localResponsesObjectState: {
        ...localResponsesObjectState,
        [fieldId]: update(responseObject, {
          message: { $set: value },
        }),
      },
    });
  };

  onResponseUpdatedAtChange = ({ updatedAt }) => {
    const { fieldId } = this.props;
    const { localResponsesObjectState } = this.state;
    const responseObject = localResponsesObjectState[fieldId] || {};

    this.setState({
      localResponsesObjectState: {
        ...localResponsesObjectState,
        [fieldId]: update(responseObject, {
          updatedAt: { $set: updatedAt },
        }),
      },
    });
  };

  onSubmitClick = async () => {
    const {
      fieldId,
      createResponse,
      getPlannerFieldResponses,
      isSinglePageAndNewUnitView,
    } = this.props;
    const { localResponsesObjectState } = this.state;

    try {
      this.setState({ isFocused: false });

      if (isSinglePageAndNewUnitView) {
        this.setState({
          isSubmittingResponse: true,
        });
      } else {
        this.setState({
          isFullScreenLoading: true,
        });
      }
      this.setState({
        localResponsesObjectState: {
          ...this.state.localResponsesObjectState,
          [fieldId]: null,
        },
      });
      await createResponse({
        fieldId,
        responseFieldData: localResponsesObjectState[fieldId],
      });

      if (getPlannerFieldResponses) {
        await getPlannerFieldResponses.refetch();
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isSinglePageAndNewUnitView) {
        this.setState({
          isSubmittingResponse: false,
        });
      } else {
        this.setState({
          isFullScreenLoading: false,
        });
      }
    }
  };

  onClickEdit = response => {
    const { fieldId, onFocusInputField } = this.props;

    this.setState({
      isEditingResponse: true,
    });

    this.setState({
      localResponsesObjectState: {
        ...this.state.localResponsesObjectState,
        [fieldId]: _.cloneDeep(response),
      },
    });

    onFocusInputField({ others: { id: `Reflection:${response.id}` } });
  };

  onClickDelete = async ({ id }) => {
    this.setState({
      showDeleteConfirmDialog: true,
      responseToDelete: { id },
    });
  };

  onConfirmDelete = async () => {
    const { responseToDelete } = this.state;
    const { fieldId, deleteResponse, getPlannerFieldResponses } = this.props;
    const { isEditingResponse } = this.state;

    if (isEditingResponse) {
      this.onCancelEdit();
    }

    try {
      this.onCloseDeleteDialog();
      await deleteResponse({ id: responseToDelete.id, fieldId });
      if (getPlannerFieldResponses) {
        await getPlannerFieldResponses.refetch();
      }
    } catch (e) {
      console.error(e);
    }
  };

  onCloseDeleteDialog = () => {
    this.setState({
      showDeleteConfirmDialog: false,
      responseToDelete: null,
    });
  };

  emptyLocalObjectInRedux = () => {
    const { fieldId, updateLocalResponses } = this.props;

    this.setState({
      localResponsesObjectState: {
        ...this.state.localResponsesObjectState,
        [fieldId]: {},
      },
    });

    updateLocalResponses({
      [fieldId]: {},
    });
  };

  onCancelEdit = e => {
    e && e.stopPropagation();
    const {
      isEditingResponse,
      isFocused,
      localResponsesObjectState,
    } = this.state;
    const { onBlurInputField, fieldId } = this.props;

    if (isEditingResponse) {
      this.setState({
        isEditingResponse: false,
      });
      onBlurInputField({
        others: { id: localResponsesObjectState[fieldId].id },
      });
    }
    if (isFocused) {
      this.setState({ isFocused: false });
    }

    this.emptyLocalObjectInRedux();
  };

  onFocusCreate = () => {
    const { isEditingResponse } = this.state;

    if (isEditingResponse) {
      this.onCancelEdit();
      this.setState({
        isFocused: true,
        responseFieldActive: true,
        isEditingResponse: false,
      });
    } else {
      this.setState({ isFocused: true, responseFieldActive: true });
    }
  };

  onBlurCreate = () => {
    this.setState({ responseFieldActive: false });
  };

  onModalClose = () => {
    this.setState({ showEvidenceSelectionModal: false });
  };

  onDropDownOptionClick = ({ key }) => {
    switch (key) {
      case "add_post":
        this.setState({ showEvidenceSelectionModal: true });
        break;
    }
  };

  onContentUploaded = params => {
    const { fieldId, t } = this.props;

    const { localResponsesObjectState } = this.state;

    const responseObject = localResponsesObjectState[fieldId] || {};
    const newAttachments = params["attachments"];
    const updatedParams = _.map(newAttachments, item => {
      let filteredItem = item;
      // We will not have name in case of recording.
      // And we will not want thumbUrl and __typename which is generated when we record audio.
      if (item.type === "AUDIO") {
        filteredItem = _.omit(item, ["thumbUrl", "__typename", ""]);
        filteredItem = {
          ...filteredItem,
          name: item.name || t("common:audio"),
        };
      }
      return {
        ...filteredItem,
        isPost: false,
      };
    });

    this.setState({
      localResponsesObjectState: {
        ...localResponsesObjectState,
        [fieldId]: update(responseObject, {
          evidence: evidence =>
            update(evidence || [], { $push: updatedParams }),
        }),
      },
    });
  };

  onDeleteEvidenceClick = ({ id }) => {
    const { fieldId } = this.props;
    const { localResponsesObjectState } = this.state;
    const responseObject = localResponsesObjectState[fieldId] || {};

    this.setState({
      localResponsesObjectState: {
        ...localResponsesObjectState,
        [fieldId]: update(responseObject, {
          evidence: {
            $set: _.filter(
              responseObject["evidence"],
              item => !_.isEqual(item.id, id)
            ),
          },
        }),
      },
    });
  };

  updateCreateContainerRef = ref => {
    this.createContainerRef = ref;
  };

  updateCreateResponseRef = ref => {
    this.createResponseRef = ref;
  };

  getSkeletonLoader = () => {
    return (
      <div className={classes.skeletonLoaderContainer}>
        <div className={classes.leftContainer}>
          <SkeletonLoader height="40px" width="40px" />
        </div>
        <div className={classes.rightContainer}>
          <SkeletonLoader height="20px" width="160px" />
          <SkeletonLoader height="20px" width="100%" />
        </div>
      </div>
    );
  };

  getCreateContainer = isCreatingResponse => {
    const {
      fieldId,
      onMediaClick,
      filteredDropDownOptions,
      t,
      userInfo,
      isSinglePageAndNewUnitView,
      isReflectionField,
    } = this.props;

    const { localResponsesObjectState } = this.state;

    const localResponseObj = localResponsesObjectState[fieldId] || {};
    const responseCreationData =
      localResponseObj && getResponseCreationDataMemoized(localResponseObj);
    const isEditMode =
      isCreatingResponse &&
      (_.get(responseCreationData, "label", false) ||
        _.get(responseCreationData, "attachments.length", 0) > 0);
    const { id, first_name, profile_image } = userInfo;

    const profileCascadeItems = [
      { id, value: first_name, imageUrl: profile_image },
    ];
    const showEditDateIcon = isReflectionCreatedAtEditEnabled();
    const updatedAtDateMomentObj = moment(localResponseObj?.updatedAt);
    const updatedAtDate = localResponseObj?.updatedAt;

    const onResponseUpdatedAtChange = ({ updatedAt }) => {
      const { fieldId } = this.props;
      const { localResponsesObjectState } = this.state;
      const responseObject = localResponsesObjectState[fieldId] || {};

      this.setState({
        localResponsesObjectState: {
          ...localResponsesObjectState,
          [fieldId]: update(responseObject, {
            updatedAt: { $set: updatedAt },
          }),
        },
      });
    };
    const chatBarProps = {};
    if (isSinglePageAndNewUnitView) {
      chatBarProps.dsVersion = "2.0";
    }

    const getDateAndTimeComponent = () => {
      const { isSinglePageAndNewUnitView } = this.props;
      const { isEditingResponse } = this.state;
      return showEditDateIcon ? (
        <div
          className={classNames(classes.dateTimeContainer, {
            "text-label": isSinglePageAndNewUnitView,
            [classes.dateTimeContainerV2]: isSinglePageAndNewUnitView,
          })}
        >
          <div
            className={
              isSinglePageAndNewUnitView ? "text-label" : "text-label-4"
            }
          >
            {t("common:date_slash_time")}
          </div>
          <div
            className={classNames(classes.datePickerContainer, {
              "text-label": isSinglePageAndNewUnitView,
              [classes.datePickerContainerV2]: isSinglePageAndNewUnitView,
            })}
          >
            <DatePicker
              picker="date"
              value={moment(localResponseObj?.updatedAt)}
              onChange={date => {
                onResponseUpdatedAtChange({ updatedAt: date });
              }}
              showTimePicker={true}
              testId="unitPlans-responseEdit"
            >
              <div className={classes.datePickerButtonContainer}>
                <div
                  className={classNames(classes.subtext, {
                    "text-label": isSinglePageAndNewUnitView,
                    "text-label-4": !isSinglePageAndNewUnitView,
                  })}
                >
                  {updatedAtDate && !isEditingResponse ? (
                    <span>
                      {moment(updatedAtDateMomentObj).format(
                        "h:mm a, Do MMM YYYY"
                      )}
                    </span>
                  ) : (
                    <span className={classes.timeDisplayText}>
                      {getDateAndTimeSince({
                        date: new Date(),
                        format: "h:mm a, Do MMM YYYY",
                        t,
                      })}
                    </span>
                  )}
                </div>
                <IconButton
                  icon={<CaretDownFilled />}
                  variant="neutral"
                  type="plain"
                  size="x-small"
                  testId="unitPlans-responseEdit-toggleDateIcon"
                  _legacy_variant="plain"
                />
              </div>
            </DatePicker>
          </div>
        </div>
      ) : null;
    };

    return (
      <div
        className={classNames({
          [classes.responseItemContainer]: !isSinglePageAndNewUnitView,
          [classes.responseItemContainerV2]:
            isSinglePageAndNewUnitView && isReflectionField,
          [classes.chatBar]: isSinglePageAndNewUnitView,
        })}
        ref={this.updateCreateContainerRef}
        onClick={this.onFocusCreate}
      >
        <div>
          <ProfileCascade
            items={profileCascadeItems}
            avatarStyles={getAvatarStyles(isSinglePageAndNewUnitView)}
            showAvatar={true}
            dsVersion={isSinglePageAndNewUnitView ? "2.0" : "1.0"}
          />
        </div>
        <div
          className={classNames(classes.chatbarContainer, {
            ["text-body-1"]: !isSinglePageAndNewUnitView,
            ["text-body"]: isSinglePageAndNewUnitView,
          })}
        >
          {ACLStore.can("FeatureFlag:EnableDateTimeInReflections")
            ? getDateAndTimeComponent()
            : null}

          <ChatbarWrapper
            {...chatBarProps}
            messageData={
              isCreatingResponse ? responseCreationData : DEFAULT_MESSAGE_DATA
            }
            sendMessage={this.onSubmitClick}
            inputTextPlaceHolder={t("common:write_your_reflection_here")}
            cancelEditMessage={this.onCancelEdit}
            collaborators={[]}
            mode={
              isSinglePageAndNewUnitView
                ? isReflectionField
                  ? "VIEW"
                  : "EDIT"
                : isEditMode
                ? "EDIT"
                : "VIEW"
            }
            selectDropDownOptions={filteredDropDownOptions}
            onDropDownOptionClick={this.onDropDownOptionClick}
            onResponseChange={this.onResponseChange}
            onAddAttachment={this.onContentUploaded}
            onDeleteMediaClick={this.onDeleteEvidenceClick}
            focus={isCreatingResponse}
            onFocus={this.onFocusCreate}
            onMediaClick={onMediaClick}
            onBlur={this.onBlurCreate}
            customDropdownRef={this.updateCreateDropDownRef}
            isStickyContainer={false}
            showAccessibilityHelperText={false}
            containerStyle={{
              ...(showEditDateIcon ? styles.chatbarContainerStyle : {}),
              ...(isSinglePageAndNewUnitView && showEditDateIcon
                ? { borderTop: "0px" }
                : {}),
            }}
            testIdPrefix="unitPlans-responseEdit"
            modalDimensions={modalDimensions}
          />
        </div>
      </div>
    );
  };

  getEmptyContainer = () => {
    const { coachMarkId, t, isSinglePageAndNewUnitView } = this.props;
    return (
      <div
        className={
          isSinglePageAndNewUnitView
            ? classes.emptyContainerV2
            : classes.emptyContainer
        }
        id={coachMarkId}
      >
        {isSinglePageAndNewUnitView ? (
          <div
            className={classes.addButtonContainer}
            onClick={this.onFocusCreate}
          >
            <AddCircleOutlined variant="link" size="xx-small" />

            <div className={classNames("text-label", classes.addAnotherButton)}>
              {t("addOngoingReflection")}
            </div>
          </div>
        ) : (
          <Button
            variant={"inline"}
            icon={<AddCircleOutlined />}
            onClick={this.onFocusCreate}
            size={"small"}
            testId="unitPlans-responseEdit-addReflection"
          >
            {t("addOngoingReflection")}
          </Button>
        )}
      </div>
    );
  };

  noDataComponent = () => {
    const { userInfo, t, defaultStudentNameOrderPreference } = this.props;
    const { childFirstName, childMiddleName, childLastName } = userInfo;
    return (
      <div className={classes.noDataContainer}>
        <img src={NoSearchResultsIllustration} alt=""></img>
        {t("unitPlan:no_reflection_added_with_username", {
          username: getFormattedStudentName({
            student: {
              firstName: childFirstName,
              middleName: childMiddleName,
              lastName: childLastName,
            },
            defaultStudentNameOrderPreference,
            isFormattedNameV2: true,
          }),
        })}
      </div>
    );
  };

  render() {
    const {
      options,
      fieldId,
      responses,
      isReflectionField,
      userType,
      curriculumType,
      isSinglePageAndNewUnitView,
      t,
      isStudentView,
    } = this.props;
    const {
      isFocused,
      isEditingResponse,
      isFullScreenLoading,
      showEvidenceSelectionModal,
      isSubmittingResponse,
      showDeleteConfirmDialog,
    } = this.state;
    const isCreatingResponse = isFocused && !isEditingResponse;
    const areResponsesLoaded = _.size(responses) > 0;
    const showCreateResponseBox =
      (isReflectionField || isCreatingResponse) &&
      !_.isEqual(userType, "parent");
    const showEmptyScreen = userType == "parent" && _.isEmpty(responses);

    return (
      <div
        className={classNames(classes.responseContainer, {
          [classes.container]: !isSinglePageAndNewUnitView,
          [classes.containerV2]:
            isSinglePageAndNewUnitView && isReflectionField && !isStudentView,
          [classes.isReflectionField]:
            !isReflectionField && isSinglePageAndNewUnitView,
        })}
        ref={ref => (this.responseContainerRef = ref)}
      >
        {isFullScreenLoading && <FullScreenLoader />}

        {showEmptyScreen && this.noDataComponent()}

        {isSinglePageAndNewUnitView &&
          !isReflectionField &&
          !isCreatingResponse &&
          this.getEmptyContainer()}

        <div
          className={classNames(classes.responseContainer, {
            [classes.responseContainerV2]: isSinglePageAndNewUnitView,
          })}
        >
          {(showCreateResponseBox || areResponsesLoaded) &&
            showCreateResponseBox &&
            this.getCreateContainer(isCreatingResponse)}
          {isSinglePageAndNewUnitView &&
            isSubmittingResponse &&
            this.getSkeletonLoader()}
          {(showCreateResponseBox || areResponsesLoaded) &&
            areResponsesLoaded &&
            this.getResponses()}
        </div>

        {!isSinglePageAndNewUnitView &&
          !isReflectionField &&
          !isCreatingResponse &&
          this.getEmptyContainer()}

        {showEvidenceSelectionModal && (
          <DSProvider dsVersion="1.0">
            <AddEvidenceModalWrapper
              fieldId={fieldId}
              onRequestClose={this.onModalClose}
              curriculumType={curriculumType}
              onUpdateLocalResponseState={this.onUpdateLocalResponseState}
              localResponsesObjectState={this.state.localResponsesObjectState}
            />
          </DSProvider>
        )}

        {showDeleteConfirmDialog && (
          <Dialog
            dsVersion="2.0"
            variant="delete-dialog"
            title={t("common:delete_with_label", {
              label: t("common:response"),
            })}
            message={t("unitPlan:delete_response_confirmation")}
            buttonOneText={t("common:cancel")}
            buttonTwoText={t("common:delete_agree")}
            onButtonOneClick={this.onCloseDeleteDialog}
            onButtonTwoClick={this.onConfirmDelete}
            onClose={this.onCloseDeleteDialog}
            setIsDialogOpen={true}
          />
        )}
      </div>
    );
  }
}

const mapActionCreatorsToProps = {
  editResponse,
  updateLocalResponses,
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const { fieldKey, parentId, curriculumType, selectedCourseIds } = ownProps;

  const userType = userInfo.user_type;
  const studentIds = [];
  if (userType != "staff") {
    userType == "student"
      ? studentIds.push(userInfo.id)
      : studentIds.push(userInfo.childID);
  }

  const responseInput = getResponseInputMemoized({
    fieldKey,
    id: parentId,
    type: "UNIT_PLAN",
    selectedCourseIds,
    studentIds,
  });

  const filteredDropDownOptions = getFilteredDropDownOptionsMemoized({
    curriculumType,
    userType,
  });
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );

  const localResponseObj = _.get(state, `planner.responses`, {});
  return {
    isData: true,
    isLoading: false,
    responseInput,
    filteredDropDownOptions,
    userType,
    userInfo,
    defaultStudentNameOrderPreference,
    localResponseObj,
  };
};

const ResponsesEditWrapper = compose(
  I18nHOC({ resource: ["unitPlan", "common"] }),
  connect(mapStateToProps, mapActionCreatorsToProps),
  graphql(getPlannerFieldResponsesQuery, {
    skip: ({ showCourseFilter, userType }) =>
      (userType == "staff" && !showCourseFilter) || userType == "student",
    name: "getPlannerFieldResponses",
    options: ({ responseInput }) => ({
      fetchPolicy: "cache-and-network",
      variables: responseInput,
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getPlannerFieldResponses,
      ownProps: { fieldId, fieldKey, responseInput, isData, isLoading },
    }) {
      const data = getPlannerFieldResponsesFromCache(responseInput);

      const fieldData = _.find(data.fields, { id: fieldId });
      const responses = _.get(fieldData, "responses", []);
      const responseNodes = _.map(
        _.get(responses, "edges", []),
        _.iteratee("node")
      );

      return {
        responses: responseNodes,
        isData: !_.isEmpty(data) && isData,
        getPlannerFieldResponses,
        onPusherEventTriggered: params => {
          if (_.includes(params.fields, fieldKey)) {
            getPlannerFieldResponses.refetch();
          }
        },
        isLoading:
          getPlannerFieldResponses["networkStatus"] == 1 ||
          getPlannerFieldResponses["networkStatus"] == 2 ||
          getPlannerFieldResponses["networkStatus"] == 4 ||
          isLoading,
      };
    },
  }),
  withPusherBind(["unitPlanFieldUpdate"]),
  withLoader
)(ResponsesEdit);

ResponsesEditWrapper.defaultProps = {
  showEvidence: true,
};

export default ResponsesEditWrapper;
